import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useCallback, useEffect, useState } from "react";
import { ErrorWithRetry, LoadingMessage } from "../../components/UiStates";
import { Button } from "../../components/ui/Button";
import { Card, CardTitle } from "../../components/ui/Card";
import { useLanguage } from "../../lib/LanguageContext";
import { useTradingMode } from "../../lib/TradingModeContext";
import { modeLabel } from "../../lib/translations";
import { apiClient } from "../../services/api/client";
const MIN_PRICE_BAND_PCT = 0.1;
const MAX_PRICE_BAND_PCT = 50;
const decimalToDisplayPct = (value) => {
    const pct = value * 100;
    return Number.isInteger(pct) ? String(pct) : pct.toFixed(2).replace(/\.?0+$/, "");
};
const parseDisplayPct = (raw) => {
    const normalized = raw.trim().replace(",", ".");
    if (!normalized)
        return null;
    const pct = Number(normalized);
    if (!Number.isFinite(pct))
        return null;
    return pct / 100;
};
export const PretradePolicyCard = () => {
    const { t, lang } = useLanguage();
    const { mode } = useTradingMode();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [runtime, setRuntime] = useState(null);
    const [priceBandPct, setPriceBandPct] = useState("");
    const [allowOpenOrders, setAllowOpenOrders] = useState(false);
    const [savedMsg, setSavedMsg] = useState(false);
    const load = useCallback(() => {
        setLoading(true);
        setError("");
        setSavedMsg(false);
        void apiClient.policies.runtime.get(mode).then((result) => {
            if (!result.success) {
                setRuntime(null);
                setError(result.error.message);
                setLoading(false);
                return;
            }
            const cfg = result.data;
            setRuntime(cfg);
            setPriceBandPct(decimalToDisplayPct(cfg.pretrade?.price_band_pct ?? 0.03));
            setAllowOpenOrders(Boolean(cfg.pretrade?.position_transfer_allow_open_orders));
            setLoading(false);
        });
    }, [mode]);
    useEffect(() => {
        load();
    }, [load]);
    const save = () => {
        setSaving(true);
        setError("");
        setSavedMsg(false);
        const priceBandDecimal = parseDisplayPct(priceBandPct);
        if (priceBandDecimal === null) {
            setError(t("settings.pretradePriceBandValidation"));
            setSaving(false);
            return;
        }
        const displayPct = priceBandDecimal * 100;
        if (displayPct < MIN_PRICE_BAND_PCT || displayPct > MAX_PRICE_BAND_PCT) {
            setError(t("settings.pretradePriceBandValidation"));
            setSaving(false);
            return;
        }
        void apiClient.policies.runtime
            .update(mode, {
            pretrade: {
                price_band_pct: priceBandDecimal,
                position_transfer_allow_open_orders: allowOpenOrders,
            },
        })
            .then((result) => {
            if (!result.success) {
                setError(result.error.message);
                setSaving(false);
                return;
            }
            setRuntime(result.data);
            setPriceBandPct(decimalToDisplayPct(result.data.pretrade?.price_band_pct ?? priceBandDecimal));
            setAllowOpenOrders(Boolean(result.data.pretrade?.position_transfer_allow_open_orders));
            setSaving(false);
            setSavedMsg(true);
        });
    };
    return (_jsxs(Card, { className: "mt-6", children: [_jsx(CardTitle, { className: "mb-2", children: t("settings.pretradeTitle") }), _jsx("p", { className: "mb-4 text-sm text-muted-foreground", children: t("settings.pretradeHint") }), _jsx("p", { className: "mb-4 rounded-md border border-border bg-muted/20 px-3 py-2 text-sm text-muted-foreground", children: t("settings.pretradeModeNote", { mode: modeLabel(lang, mode) }) }), loading ? _jsx(LoadingMessage, { text: t("risk.load") }) : null, error ? _jsx(ErrorWithRetry, { message: error, onRetry: load, disabled: saving, className: "mb-4" }) : null, !loading && !error && runtime ? (_jsxs(_Fragment, { children: [_jsxs("div", { className: "mb-4 grid gap-3 sm:grid-cols-2", children: [_jsxs("label", { className: "flex flex-col gap-2 text-sm", children: [_jsx("span", { className: "text-muted-foreground", children: t("settings.pretradePriceBand") }), _jsxs("div", { className: "flex items-center gap-2", children: [_jsx("input", { className: "h-10 flex-1 rounded-md border border-border bg-background px-3 text-foreground", inputMode: "decimal", value: priceBandPct, onChange: (e) => setPriceBandPct(e.target.value), disabled: saving, "aria-describedby": "pretrade-price-band-hint" }), _jsx("span", { className: "text-muted-foreground", children: "%" })] }), _jsx("span", { id: "pretrade-price-band-hint", className: "text-xs text-muted-foreground", children: t("settings.pretradePriceBandHint") })] }), _jsxs("label", { className: "flex flex-col gap-2 text-sm", children: [_jsx("span", { className: "text-muted-foreground", children: t("settings.pretradeTransferOpenOrders") }), _jsxs("select", { className: "h-10 rounded-md border border-border bg-background px-3 text-foreground", value: allowOpenOrders ? "allow" : "forbid", onChange: (e) => setAllowOpenOrders(e.target.value === "allow"), disabled: saving, "aria-describedby": "pretrade-transfer-open-hint", children: [_jsx("option", { value: "forbid", children: t("settings.pretradeTransferForbid") }), _jsx("option", { value: "allow", children: t("settings.pretradeTransferAllow") })] }), _jsx("span", { id: "pretrade-transfer-open-hint", className: "text-xs text-muted-foreground", children: t("settings.pretradeTransferOpenOrdersHint") })] })] }), _jsxs("div", { className: "flex flex-wrap items-center gap-3", children: [_jsx(Button, { type: "button", onClick: save, disabled: saving, children: saving ? t("common.loading") : t("settings.pretradeSave") }), savedMsg ? (_jsx("span", { className: "text-sm text-emerald-600 dark:text-emerald-400", children: t("settings.pretradeSaved") })) : null] })] })) : null] }));
};
